/**
 * Image compression utility for content calendar uploads
 * Compresses images to optimize for both storage and OpenAI API compatibility
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 to 1.0
  maxSizeKB?: number; // Target max size in KB
  format?: 'jpeg' | 'png' | 'webp';
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Compress an image file
 * @param file - The original image file
 * @param options - Compression options
 * @returns Promise with compression result
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    maxSizeKB = 2048, // 2MB default for OpenAI compatibility
    format = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    // Skip compression for non-image files
    if (!file.type.startsWith('image/')) {
      resolve({
        file,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1
      });
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspectRatio;
        }

        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // Check if we need further compression
              let currentBlob = blob;

              // If still too large, reduce quality iteratively
              const compressMore = async (currentQuality: number): Promise<Blob> => {
                return new Promise((resolveBlob) => {
                  canvas.toBlob(
                    (newBlob) => {
                      if (!newBlob) {
                        resolveBlob(currentBlob);
                        return;
                      }

                      if (newBlob.size <= maxSizeKB * 1024 || currentQuality <= 0.1) {
                        resolveBlob(newBlob);
                      } else {
                        // Reduce quality and try again
                        compressMore(currentQuality - 0.1).then(resolveBlob);
                      }
                    },
                    `image/${format}`,
                    currentQuality
                  );
                });
              };

              // Apply iterative compression if needed
              if (blob.size > maxSizeKB * 1024) {
                compressMore(quality - 0.1).then((finalBlob) => {
                  const compressedFile = new File(
                    [finalBlob],
                    `compressed_${file.name.replace(/\.[^/.]+$/, '')}.${format}`,
                    {
                      type: `image/${format}`,
                      lastModified: Date.now()
                    }
                  );

                  resolve({
                    file: compressedFile,
                    originalSize: file.size,
                    compressedSize: compressedFile.size,
                    compressionRatio: file.size / compressedFile.size
                  });
                });
              } else {
                const compressedFile = new File(
                  [currentBlob],
                  `compressed_${file.name.replace(/\.[^/.]+$/, '')}.${format}`,
                  {
                    type: `image/${format}`,
                    lastModified: Date.now()
                  }
                );

                resolve({
                  file: compressedFile,
                  originalSize: file.size,
                  compressedSize: compressedFile.size,
                  compressionRatio: file.size / compressedFile.size
                });
              }
            },
            `image/${format}`,
            quality
          );
        } else {
          reject(new Error('Canvas context not available'));
        }
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load the image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Compress multiple images in parallel
 * @param files - Array of image files
 * @param options - Compression options
 * @returns Promise with array of compression results
 */
export const compressImages = async (
  files: File[],
  options: CompressionOptions = {}
): Promise<CompressionResult[]> => {
  const compressionPromises = files.map(file => compressImage(file, options));
  return Promise.all(compressionPromises);
};

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get compression options optimized for different use cases
 */
export const getCompressionPresets = () => ({
  // For OpenAI API compatibility (strict limits)
  openai: {
    maxWidth: 1024,
    maxHeight: 1024,
    quality: 0.8,
    maxSizeKB: 2048, // 2MB limit
    format: 'jpeg' as const
  },
  
  // For social media (good quality, reasonable size)
  social: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.85,
    maxSizeKB: 5120, // 5MB
    format: 'jpeg' as const
  },
  
  // For thumbnails (small size)
  thumbnail: {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.7,
    maxSizeKB: 200, // 200KB
    format: 'jpeg' as const
  },
  
  // High quality (minimal compression)
  high: {
    maxWidth: 2560,
    maxHeight: 1440,
    quality: 0.95,
    maxSizeKB: 10240, // 10MB
    format: 'jpeg' as const
  }
}); 